import { Activity } from 'lucide-react';
import { FIXED_FAZE_META, computeFazeTimes } from '../spolecne';

/**
 * Editor jedné editovatelné fáze šablony/směny (Zahájení / Ukončení).
 * Name/barva/ikona jsou pevné dle FIXED_FAZE_META. Edituje se délka a aktivity
 * uvnitř (úkol / checklist). Přestávky se řeší samostatným oddílem.
 */
export default function FazeEditor({ faze, allFaze, smenaOd, smenaDo, checklistSablony, isDark, mutedColor, iStyle, onChange }) {
    const meta = FIXED_FAZE_META[faze.id] || { nazev: faze.id, barva: '#666', Icon: Activity };
    const IkonaComp = meta.Icon;
    const aktivniSab = (checklistSablony || []).filter(cs => cs.stav === 'aktivni');
    const casy = computeFazeTimes(smenaOd, smenaDo, allFaze);
    const myTimes = casy[faze.id] || {};

    const update = (patch) => onChange({ ...faze, ...patch });

    const addActivity = (typ) => {
        const nova = {
            typ,
            nazev: '',
            cas_od: myTimes.cas_od ?? '',
            cas_do: myTimes.cas_do ?? '',
            sablona_id: null,
            placena: false,
        };
        onChange({ ...faze, aktivity: [...(faze.aktivity || []), nova] });
    };

    const updateAct = (idx, patch) => {
        const a = [...(faze.aktivity || [])];
        a[idx] = { ...a[idx], ...patch };
        update({ aktivity: a });
    };

    const removeAct = (idx) => {
        update({ aktivity: (faze.aktivity || []).filter((_, i) => i !== idx) });
    };

    const typIcons = { ukol: '📋', checklist: '✅' };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10, padding: '12px 14px', borderRadius: 9, background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderLeft: `3px solid ${meta.barva}` }}>
            {/* Header: ikona + pevný název + čas + délka */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ width: 34, height: 34, borderRadius: 6, background: `${meta.barva}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: meta.barva, flexShrink: 0 }}>
                    <IkonaComp size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 140 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#fff' : 'var(--text-primary)' }}>{meta.nazev}</div>
                    <div style={{ fontSize: 11, color: mutedColor, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
                        {myTimes.cas_od || '—'} – {myTimes.cas_do || '—'}
                    </div>
                </div>
                <label style={{ fontSize: 11, color: mutedColor, display: 'flex', alignItems: 'center', gap: 6 }}>
                    Délka
                    <input type="number" min={0} max={240} step={5} value={faze.delka_min ?? 15}
                        onChange={e => update({ delka_min: Math.max(0, Number(e.target.value) || 0) })}
                        style={{ ...iStyle, width: 80 }} />
                    <span>min</span>
                </label>
            </div>

            {/* Aktivity uvnitř fáze */}
            <div style={{ paddingTop: 6, borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Aktivity ({(faze.aktivity || []).length})
                    </span>
                    <div style={{ display: 'flex', gap: 4 }}>
                        {[
                            { typ: 'ukol', label: '+ Úkol', color: '#F59E0B' },
                            { typ: 'checklist', label: '+ Checklist', color: '#10B981' },
                        ].map(btn => (
                            <button key={btn.typ} type="button" onClick={() => addActivity(btn.typ)}
                                style={{ fontSize: 10, background: `${btn.color}18`, color: btn.color, border: 'none', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontWeight: 600 }}>
                                {btn.label}
                            </button>
                        ))}
                    </div>
                </div>

                {(faze.aktivity || []).length === 0 && (
                    <div style={{ textAlign: 'center', padding: 10, color: mutedColor, fontSize: 11 }}>Žádné aktivity v této fázi</div>
                )}

                {(faze.aktivity || []).map((a, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, padding: '6px 8px', borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)' }}>
                        <span style={{ fontSize: 14, flexShrink: 0 }}>{typIcons[a.typ] || '•'}</span>
                        {a.typ === 'checklist' ? (
                            <select value={a.sablona_id || ''} onChange={e => {
                                const val = e.target.value;
                                const s = val ? aktivniSab.find(x => x.id == val) : null;
                                updateAct(idx, { sablona_id: val ? Number(val) : null, nazev: s ? s.nazev : a.nazev });
                            }} style={{ ...iStyle, flex: 1 }}>
                                <option value="">— Vyberte checklist —</option>
                                {aktivniSab.map(s => <option key={s.id} value={s.id}>{s.nazev}</option>)}
                            </select>
                        ) : (
                            <input value={a.nazev || ''} onChange={e => updateAct(idx, { nazev: e.target.value })}
                                placeholder="Název úkolu" style={{ ...iStyle, flex: 1 }} />
                        )}
                        <button type="button" onClick={() => removeAct(idx)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: 14, flexShrink: 0, padding: 2 }}>✕</button>
                    </div>
                ))}
            </div>
        </div>
    );
}
